import { useCallback, useEffect, useMemo, useState } from "react";
import { Spin } from "antd";
import TotalUser from "../../../assets/dashboard/TotalUser.jsx";
import Accounts from "../../../assets/dashboard/Accounts.jsx";
import Active from "../../../assets/dashboard/Active.jsx";
import KYC from "../../../assets/dashboard/KYC.jsx";
import Deposit from "../../../assets/wallet/Deposit.jsx";
import Withdraw from "../../../assets/wallet/Withdraw.jsx";
import Pending from "../../../assets/common/Pending.jsx";
import Approved from "../../../assets/common/Approved.jsx";
import AreaChart from "../../../components/global/charts/AreaChart.jsx";
import PieChart from "../../../components/global/charts/PieChart.jsx";
import { GetAllCountryApi } from "../../../api/request/common.js";
import {
  GetAllOffices,
  GetAllTeamLeaders,
} from "../../../api/request/subadmin.js";
import {
  GetSalesAgentsApi,
  buildSalesAgentsParams,
  mapSalesAgentsToOptions,
  GetSalesDepositTrendsApi,
  GetSalesReportApi,
  GetSalesSourceMixApi,
  GetTopPerformingAgentsApi,
  GetTopPerformingTeamLeadersApi,
  GetAssignedSalesOfficeApi,
} from "../../../api/request/salesDashboard/dashboard.js";
import DashboardFilters from "./components/DashboardFilters.jsx";
import StatisticsCard from "./components/StatisticsCard.jsx";
import TopPerformingAgentsTable from "./components/TopPerformingAgentsTable.jsx";
import TopPerformingTeamLeaders from "./components/TopPerformingTeamLeaders.jsx";
import TradesSection from "./components/TradesSection.jsx";
import { LinkOutlined } from "@ant-design/icons";
import { useUserStore } from "../../../store/userStore.js";
import { getResponseData } from "../../../utils/apiHelpers.js";
import CopyText from "../../../components/copy/CopyText.jsx";
import { CLIENT_URL } from "../../../utils/constants.js";
import SalesDashboardHeader from "./components/SalesDashboardHeader.jsx";

const SOURCE_TYPES = ["DIRECT", "IB", "SALES"];

const formatCurrency = (value, precision = 0) =>
  `$${Number(value || 0).toLocaleString("en-US", { maximumFractionDigits: precision })}`;

const formatPercent = (value) =>
  `${Number(value || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;

export default function Dashboard() {
  const [isLoading, setIsLoading] = useState(false);
  const userDetails = useUserStore((state) => state.userDetails);
  const isSales = userDetails?.department === "Sales";
  const isSuperAdmin =
    userDetails?.roleName?.toLowerCase() === "admin" ||
    userDetails?.roleId == 1;
  const refferalLink =
    isSales && userDetails?.referralCode
      ? `${CLIENT_URL}/?tab=register&refId=${userDetails?.referralCode}`
      : null;
  const isAgent = isSales && !userDetails?.isTeamLeader;
  const isTeamLeader = isSales && userDetails?.isTeamLeader;
  const teamLeadId =
    isSales && userDetails?.isTeamLeader ? userDetails?.userId : null;
  const [offices, setOffices] = useState([]);
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [agents, setAgents] = useState([]);
  const [countries, setCountries] = useState([]);
  const [secondaryOffices, setSecondaryOffices] = useState([]);
  const [isSecondaryOfficesLoading, setIsSecondaryOfficesLoading] =
    useState(false);
  const [sources, setSources] = useState([]);

  const [filters, setFilters] = useState({
    from: undefined,
    to: undefined,
    officeId: undefined,
    teamLeadId: teamLeadId,
    agentId: undefined,
    countryId: undefined,
    source: undefined,
    secondaryOfficeId: undefined,
  });

  const [metrics, setMetrics] = useState({});
  const [depositTrends, setDepositTrends] = useState([]);
  const [sourceMix, setSourceMix] = useState([]);
  const [topAgents, setTopAgents] = useState([]);
  const [topTeamLeaders, setTopTeamLeaders] = useState([]);

  const getCommonParams = useCallback(
    ({ includeSource = false, includeTop = false } = {}) => {
      const params = {
        from: filters.from,
        to: filters.to,
        officeId: filters.officeId,
        teamLeadId: filters.teamLeadId,
        agentId: filters.agentId,
        countryId: filters.countryId,
        secondaryOfficeId: filters.secondaryOfficeId,
      };

      if (includeSource) params.source = filters.source;
      if (includeTop) params.top = 5;

      return Object.fromEntries(
        Object.entries(params).filter(
          ([, value]) => value !== undefined && value !== null && value !== "",
        ),
      );
    },
    [filters],
  );

  const fetchFilterOptions = async () => {
    try {
      const [officesRes, countriesRes] = await Promise.all([
        GetAllOffices(),
        GetAllCountryApi(),
      ]);

      if (officesRes?.data?.success) {
        setOffices(
          (officesRes?.data?.data || []).map((item) => ({
            label: item?.officeName || item?.name,
            value: item?.id || item?.officeId,
          })),
        );
      }
      if (countriesRes?.data?.success) {
        setCountries(
          (countriesRes?.data?.data || []).map((item) => ({
            label: item?.countryName,
            value: item?.id,
          })),
        );
      }
      setSources(
        SOURCE_TYPES.map((type) => ({
          label: type,
          value: type,
        })),
      );
    } catch (error) {
      setSources(
        SOURCE_TYPES.map((type) => ({
          label: type,
          value: type,
        })),
      );
    }
  };

  const fetchSecondaryOffices = async () => {
    if (!isSales) {
      setSecondaryOffices([]);
      setIsSecondaryOfficesLoading(false);
      return;
    }
    try {
      setIsSecondaryOfficesLoading(true);
      const data = getResponseData(await GetAssignedSalesOfficeApi());
      if (data?.success) {
        setSecondaryOffices(
          (data?.data?.secondaryOffices || []).map((item) => ({
            label: item?.officeName,
            value: item?.secondaryOfficeId,
          })),
        );
      } else {
        setSecondaryOffices([]);
      }
    } catch (error) {
      setSecondaryOffices([]);
    } finally {
      setIsSecondaryOfficesLoading(false);
    }
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      setIsLoading(true);
      const commonParams = getCommonParams();
      const salesReportParams = getCommonParams({ includeSource: true });
      const agentParams = getCommonParams({ includeTop: true });
      const teamLeaderParams = getCommonParams({ includeTop: true });
      const showTopAgents = isSuperAdmin || isTeamLeader;
      const showTopTeamLeaders = isSuperAdmin;

      delete teamLeaderParams.agentId;
      delete teamLeaderParams.countryId;
      delete teamLeaderParams.source;
      delete teamLeaderParams.teamLeadId;

      delete agentParams.countryId;
      delete agentParams.source;

      const [
        reportRes,
        depositTrendsRes,
        sourceMixRes,
        topAgentsRes,
        topTeamLeadersRes,
      ] = await Promise.allSettled([
        GetSalesReportApi(new URLSearchParams(salesReportParams).toString()),
        GetSalesDepositTrendsApi(new URLSearchParams(commonParams).toString()),
        GetSalesSourceMixApi(new URLSearchParams(commonParams).toString()),
        ...(showTopAgents
          ? [
              GetTopPerformingAgentsApi(
                new URLSearchParams(agentParams).toString(),
              ),
            ]
          : [Promise.resolve({ data: { success: false } })]),
        ...(showTopTeamLeaders
          ? [
              GetTopPerformingTeamLeadersApi(
                new URLSearchParams(teamLeaderParams).toString(),
              ),
            ]
          : [Promise.resolve({ data: { success: false } })]),
      ]);

      if (reportRes.status === "fulfilled" && reportRes.value?.data?.success) {
        setMetrics(reportRes.value?.data?.data?.metrics || {});
      } else {
        setMetrics({});
      }

      if (
        depositTrendsRes.status === "fulfilled" &&
        depositTrendsRes.value?.data?.success
      ) {
        setDepositTrends(depositTrendsRes.value?.data?.data || []);
      } else {
        setDepositTrends([]);
      }

      if (
        sourceMixRes.status === "fulfilled" &&
        sourceMixRes.value?.data?.success
      ) {
        setSourceMix(sourceMixRes.value?.data?.data || []);
      } else {
        setSourceMix([]);
      }

      if (
        topAgentsRes.status === "fulfilled" &&
        topAgentsRes.value?.data?.success
      ) {
        setTopAgents(topAgentsRes.value?.data?.data || []);
      } else {
        setTopAgents([]);
      }

      if (
        topTeamLeadersRes.status === "fulfilled" &&
        topTeamLeadersRes.value?.data?.success
      ) {
        setTopTeamLeaders(topTeamLeadersRes.value?.data?.data || []);
      } else {
        setTopTeamLeaders([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [getCommonParams, isSuperAdmin, isTeamLeader]);

  const fetchTeamLeaders = async () => {
    if (!filters.officeId) {
      setTeamLeaders([]);
      return;
    }
    try {
      const data = getResponseData(await GetAllTeamLeaders(filters.officeId));
      if (data?.success) {
        setTeamLeaders(
          (data?.data || []).map((item) => ({
            label: item?.fullName || item?.teamLeadName,
            value: item?.userId || item?.teamLeadId,
          })),
        );
      }
    } catch (error) {
      setTeamLeaders([]);
    }
  };

  const fetchAgents = async () => {
    if (!filters.teamLeadId) {
      setAgents([]);
      return;
    }
    try {
      const data = getResponseData(
        await GetSalesAgentsApi(buildSalesAgentsParams(filters)),
      );
      if (data?.success) {
        setAgents(mapSalesAgentsToOptions(data?.data));
      }
    } catch (error) {
      setAgents([]);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
    fetchSecondaryOffices();
  }, [isSales]);

  useEffect(() => {
    fetchTeamLeaders();
  }, [filters.officeId]);

  useEffect(() => {
    fetchAgents();
  }, [filters.teamLeadId, filters.agentId, userDetails?.userId]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const depositTrendChart = useMemo(
    () => ({
      categories: depositTrends.map((item) => item?.label || item?.date),
      series: [
        {
          name: "Deposits",
          data: depositTrends.map((item) => Number(item?.deposits || 0)),
        },
        {
          name: "Withdrawals",
          data: depositTrends.map((item) => Number(item?.withdrawals || 0)),
        },
      ],
    }),
    [depositTrends],
  );

  const sourceMixChartData = useMemo(() => {
    const normalized = (sourceMix || []).map((item) => ({
      label: item?.source || "--",
      count: Number(item?.count || 0),
      percentage:
        item?.percentage !== undefined && item?.percentage !== null
          ? Number(item?.percentage || 0)
          : null,
    }));

    const totalCount = normalized.reduce((sum, item) => sum + item.count, 0);

    return normalized.map((item) => ({
      ...item,
      chartValue:
        item.percentage !== null
          ? item.percentage
          : totalCount > 0
            ? Number(((item.count / totalCount) * 100).toFixed(2))
            : 0,
    }));
  }, [sourceMix]);

  const sourceMixHasData = sourceMixChartData.some((item) => item.count > 0);
  const sourceMixLabels = sourceMixChartData.map((item) => item.label);
  const sourceMixValues = sourceMixChartData.map((item) => item.chartValue);
  const sourceMixColors = [
    "#2563eb",
    "#14b8a6",
    "#eab308",
    "#a855f7",
    "#f97316",
  ];

  const metricCards = [
    {
      label: "Clients Acquired",
      value: metrics?.clientsAcquired || 0,
      icon: <TotalUser color="#7367F0" />,
      bgColor: "bg-[#7367f038]",
    },
    {
      label: "Total FTDs",
      value: formatCurrency(metrics?.totalFtds, 2),
      icon: <Approved color="#28C76F" />,
      bgColor: "bg-[#28c76f30]",
    },
    {
      label: "Total Deposits",
      value: formatCurrency(metrics?.totalDeposits, 2),
      icon: <Deposit color="#28C76F" />,
      bgColor: "bg-[#28c76f30]",
    },
    {
      label: "Total Withdrawals",
      value: formatCurrency(metrics?.totalWithdrawal, 2),
      icon: <Withdraw color="#FF5964" />,
      bgColor: "bg-[#ff4c5130]",
    },
    {
      label: "Net Money In",
      value: formatCurrency(metrics?.netDeposit, 2),
      icon: <Active color="#28C76F" />,
      bgColor: "bg-[#28c76f30]",
    },
    {
      label: "Trading Volume",
      value: formatCurrency(metrics?.totalVolume, 2),
      icon: <Accounts color="#FF9F43" />,
      bgColor: "bg-[#ff9f432b]",
    },
    {
      label: "Conversion Ratio",
      value: formatPercent(metrics?.conversionRatio),
      icon: <Pending color="#FF9F43" />,
      bgColor: "bg-[#ff9f432b]",
    },
    {
      label: "Active vs Inactive",
      value: `${metrics?.activeClients || 0} / ${metrics?.inactiveClients || 0}`,
      // sub: metrics?.totalClients
      //   ? `${Math.round(
      //       ((metrics?.activeClients || 0) / metrics.totalClients) * 100,
      //     )}% ${t("active")}`
      //   : "",
      icon: <KYC color="#FF4C51" />,
      bgColor: "bg-[#ff4c5130]",
    },
  ];

  const topAgentsColumns = [
    {
      title: "Agent",
      dataIndex: "agentName",
      key: "agentName",
      render: (v) => v || "--",
    },
    {
      title: "Team",
      dataIndex: "teamName",
      key: "teamName",
      render: (v) => v || "--",
    },
    {
      title: "Deposits",
      dataIndex: "totalDeposits",
      key: "totalDeposits",
      render: (v) => formatCurrency(v),
    },
    {
      title: "Clients",
      dataIndex: "totalClients",
      key: "totalClients",
      render: (v) => v || 0,
    },
    {
      title: "FTD",
      dataIndex: "totalFtd",
      key: "totalFtd",
      render: (v) => v || 0,
    },
    {
      title: "Conv.",
      dataIndex: "conversionRatio",
      key: "conversionRatio",
      render: (v) => formatPercent(v),
    },
  ];

  return (
    <div className="space-y-5">
      {isSales && (
        <SalesDashboardHeader
          isTeamLeader={isTeamLeader}
          userDetails={userDetails}
        />
      )}

      {refferalLink && (
        <div className="border-2 border-light rounded-[10px] p-4 flex items-center gap-3 flex-wrap">
          <div className="bg-[#7367f038] p-2 rounded-lg flex items-center justify-center">
            <LinkOutlined className="text-[#7367F0] text-lg" />
          </div>
          <span className="font-semibold text-heading">Refferal Link:</span>
          <CopyText text={refferalLink}>
            <span className="text-primary break-all">{refferalLink}</span>
          </CopyText>
        </div>
      )}
      <DashboardFilters
        isTeamLeader={isTeamLeader}
        isAgent={isAgent}
        isSales={isSales}
        filters={filters}
        setFilters={setFilters}
        offices={offices}
        teamLeaders={teamLeaders}
        agents={agents}
        countries={countries}
        sources={sources}
        secondaryOffices={secondaryOffices}
        isSecondaryOfficesLoading={isSecondaryOfficesLoading}
        showCountrySource
        filterTitle={
          isTeamLeader
            ? "Team Leader Filters"
            : isAgent
              ? "My Performance Filters"
              : "Filters"
        }
      />

      {isLoading ? (
        <div className="flex justify-center flex-col my-20">
          <Spin size="large" />
          <p className="primary text-center mt-2">Loading...</p>
        </div>
      ) : (
        <>
          <div className="border-2 border-light rounded-[10px] p-5">
            <h2 className="text-title font-semibold mb-5">
              {isTeamLeader ? "Team Statistics" : isAgent ? "My Statistics" : "Statistics"}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-4 gap-5">
              {metricCards.slice(0, 4).map((card) => (
                <StatisticsCard
                  key={card.label}
                  title={card.label}
                  value={card.value}
                  subValue={card.sub}
                  icon={card.icon}
                  bgColor={card.bgColor}
                />
              ))}
            </div>
          </div>

          <div className="border-2 border-light rounded-[10px] p-5">
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-4 gap-5">
              {metricCards.slice(4).map((card) => (
                <StatisticsCard
                  key={card.label}
                  title={card.label}
                  value={card.value}
                  subValue={card.sub}
                  icon={card.icon}
                  bgColor={card.bgColor}
                />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
            <div className="xl:col-span-2 border-2 border-light rounded-[10px] p-5 overflow-hidden">
              <h2 className="text-title font-semibold">
                {isTeamLeader
                  ? "Team Deposit Trends"
                  : isAgent
                    ? "My Deposit Trends"
                    : "Deposit Trends"}
              </h2>
              <p className="text-gray-500 text-sm mb-4">
                {isTeamLeader
                  ? "Daily team deposits vs withdrawals"
                  : isAgent
                    ? "Daily personal deposits vs withdrawals"
                    : "Daily deposits vs withdrawals"}
              </p>
              <AreaChart
                data={depositTrendChart}
                colors={["#2563eb", "#ef4444"]}
                height={300}
              />
            </div>
            <div className="border-2 border-light rounded-[10px] p-5 overflow-hidden">
              <h2 className="text-title font-semibold">
                {isTeamLeader ? "Team Source Mix" : isAgent ? "My Source Mix" : "Source Mix"}
              </h2>
              <p className="text-gray-500 text-sm mb-4">
                {isTeamLeader
                  ? "Team lead source distribution"
                  : isAgent
                    ? "Your lead source distribution"
                    : "Lead source distribution"}
              </p>
              {sourceMixHasData ? (
                <>
                  <PieChart
                    values={sourceMixValues}
                    labels={sourceMixLabels}
                    colors={sourceMixColors}
                    height={240}
                    donutSize="68%"
                  />
                  <div className="flex flex-wrap items-center justify-center gap-4 mt-3">
                    {sourceMixChartData.map((item, idx) => (
                      <div
                        key={`${item.label}-${idx}`}
                        className="flex items-center gap-2 text-sm"
                      >
                        <span
                          className="w-3 h-3 rounded-full shrink-0"
                          style={{
                            backgroundColor:
                              sourceMixColors[idx % sourceMixColors.length],
                          }}
                        />
                        <span>
                          {item.label} ({item.count} /{" "}
                          {formatPercent(item.chartValue)})
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-description mt-8 text-center">No data</p>
              )}
            </div>
          </div>

          <TradesSection />

          {isTeamLeader && !isSuperAdmin && (
            <TopPerformingAgentsTable
              columns={topAgentsColumns}
              data={topAgents}
              loading={isLoading}
              rowKey="agentId"
            />
          )}

          {isSuperAdmin && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <TopPerformingAgentsTable
                columns={topAgentsColumns}
                data={topAgents}
                loading={isLoading}
                rowKey="agentId"
              />
              <TopPerformingTeamLeaders
                data={topTeamLeaders}
                loading={isLoading}
                rowKey="teamLeaderId"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
